/**
 * The Supabase client, shared by the sync engine and the portal.
 *
 * Returns null when the project is not configured. That is a supported state,
 * not an error: the front desk must run perfectly with no cloud at all, and
 * the sync engine simply reports "not configured" instead of throwing.
 */
import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

function looksConfigured(): boolean {
  return Boolean(
    url &&
      anonKey &&
      !url.includes('your-project-ref') &&
      !anonKey.includes('your-anon-key')
  );
}

export const isSupabaseConfigured = looksConfigured();

let client: SupabaseClient | null = null;

/**
 * Upper bound on any single request. Generous — a pull page is 500 rows.
 *
 * Without it, a request on a half-dead connection (a flaky router, a captive
 * portal) can hang for minutes. The sync engine runs one pass at a time, so
 * one hung request silently stops every later sync until the app restarts,
 * with the pill still claiming changes are "Sending…".
 */
const REQUEST_TIMEOUT_MS = 30_000;

function fetchWithTimeout(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const timeout = AbortSignal.timeout(REQUEST_TIMEOUT_MS);
  const signal = init.signal ? AbortSignal.any([init.signal, timeout]) : timeout;
  return fetch(input, { ...init, signal });
}

export function getSupabase(): SupabaseClient | null {
  if (!isSupabaseConfigured) return null;
  if (!client) {
    client = createClient(url!, anonKey!, {
      global: { fetch: fetchWithTimeout },
      auth: {
        persistSession: true,
        autoRefreshToken: true,
      },
      // The front desk syncs on a timer; realtime would be a second, redundant
      // channel to keep alive on a machine that is often offline.
      realtime: { params: { eventsPerSecond: 1 } },
    });
  }
  return client;
}

/** Tables that sync, in dependency order — parents before children. */
export const SYNC_TABLES = [
  'members',
  'price_schedule',
  'coach_assignments',
  'coaches',
  'app_settings',
  'subscriptions',
  'payments',
  // After subscriptions: a freeze references one, so the parent must exist.
  'freezes',
  'walk_ins',
] as const;

export type SyncTable = (typeof SYNC_TABLES)[number];
